import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { IntelEntry, intelEntries, jurisdictions } from "../data/intelligence";
import { buildJurisdictionOverviews, searchEntries } from "../utils/intelligence";

export function useIntelligenceSearch() {
  const [params, setParams] = useSearchParams();
  const [query, setQueryState] = useState(params.get("q") ?? "");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const allEntries = useMemo<IntelEntry[]>(() => [...intelEntries, ...buildJurisdictionOverviews(jurisdictions)], []);
  const isSearching = query.trim().length > 0;

  const results = useMemo(
    () =>
      isSearching
        ? searchEntries(query, allEntries)
        : [...intelEntries].sort((a, b) => b.updated.localeCompare(a.updated)).slice(0, 6),
    [query, isSearching, allEntries],
  );

  const selected = results.find((r) => r.id === selectedId) ?? results[0] ?? null;

  const setQuery = useCallback(
    (value: string) => {
      setQueryState(value);
      setSelectedId(null);
      const next = new URLSearchParams(params);
      if (value.trim()) next.set("q", value);
      else next.delete("q");
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const selectEntry = useCallback(
    (id: string) => {
      if (results.some((r) => r.id === id)) {
        setSelectedId(id);
        return;
      }
      const entry = allEntries.find((e) => e.id === id);
      if (!entry) return;
      setQuery(entry.title);
      setSelectedId(id);
    },
    [results, allEntries, setQuery],
  );

  const findEntry = useCallback((id: string) => allEntries.find((e) => e.id === id), [allEntries]);

  return { query, setQuery, results, selected, selectEntry, findEntry, isSearching };
}

export type IntelligenceSearch = ReturnType<typeof useIntelligenceSearch>;
